/**
 * Capstone Projects Dashboard
 * Main entry point for Capstone Project Framework
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Tab,
  Tabs
} from '@mui/material';
import {
  Add as AddIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  PlayArrow as PlayIcon,
  MenuBook as CaseStudyIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import capstoneService from '../services/capstoneService';
import { CapstoneProject, CapstoneProjectStatus } from '../types/capstone';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CapstoneProjects: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<CapstoneProject[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await capstoneService.getProjects();
      console.log('CapstoneProjects - Loaded projects:', response);
      console.log('CapstoneProjects - First project:', response[0]);
      console.log('CapstoneProjects - First project ID:', response[0]?.projectId);
      setProjects(response);
      setLoading(false);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load capstone projects');
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateProject = () => {
    navigate('/admin/capstone/new');
  };

  const handleViewProject = (projectId: string) => {
    console.log('CapstoneProjects - Navigating to project:', projectId);
    navigate(`/admin/capstone/projects/${projectId}`);
  };

  const getStatusColor = (status: CapstoneProjectStatus): string => {
    switch (status) {
      case CapstoneProjectStatus.DRAFT:
        return '#9e9e9e';
      case CapstoneProjectStatus.IN_PROGRESS:
        return '#2196f3';
      case CapstoneProjectStatus.UNDER_REVIEW:
        return '#ff9800';
      case CapstoneProjectStatus.COMPLETED:
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  };

  const calculateOverallProgress = (project: CapstoneProject): number => {
    if (!project.modules || project.modules.length === 0) return 0;
    const totalProgress = project.modules.reduce((sum, module) => sum + module.completionPercentage, 0);
    return Math.round(totalProgress / project.modules.length);
  };

  const activeProjects = projects.filter(p => p.status === CapstoneProjectStatus.IN_PROGRESS);
  const completedProjects = projects.filter(p => p.status === CapstoneProjectStatus.COMPLETED);
  const draftProjects = projects.filter(p => p.status === CapstoneProjectStatus.DRAFT);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
              Capstone Projects
            </Typography>
            <Typography variant="body1" color="text.secondary">
              UC Berkeley Data Strategy Framework - Apply data strategy principles to real USCIS use cases
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateProject}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
          >
            New Capstone Project
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #2196f3' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SchoolIcon sx={{ color: '#2196f3', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#2196f3' }}>
                  Active Projects
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {activeProjects.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #4caf50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrophyIcon sx={{ color: '#4caf50', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#4caf50' }}>
                  Completed
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {completedProjects.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Certificates earned
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#ff9800' }}>
                  Avg Progress
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {projects.length > 0
                  ? Math.round(projects.reduce((sum, p) => sum + calculateOverallProgress(p), 0) / projects.length)
                  : 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overall completion
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PlayIcon sx={{ color: '#9c27b0', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#9c27b0' }}>
                  Total Projects
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {projects.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All time
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Featured Case Study */}
      <Card 
        sx={{ 
          mb: 4,
          background: 'linear-gradient(135deg, #003366 0%, #005a9c 100%)',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 8
          }
        }}
        onClick={() => navigate('/admin/case-studies/maf')}
        role="button"
        tabIndex={0}
        aria-label="View MAF Case Study"
      >
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto'
                }}
              >
                <CaseStudyIcon sx={{ fontSize: 60, color: '#fff' }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip 
                  label="Featured Case Study" 
                  size="small" 
                  sx={{ 
                    bgcolor: '#ffd700', 
                    color: 'primary.main', 
                    fontWeight: 'bold',
                    mr: 1
                  }} 
                />
                <Chip 
                  label="UC Berkeley Expand" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: '#fff',
                    mr: 1
                  }} 
                />
                <Chip 
                  label="DHS Aligned" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: '#fff'
                  }} 
                />
              </Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
                Majid Al Futtaim: Analytics-Driven Transformation
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                Learn how a $9.6B MENA conglomerate built organizational resilience through analytics, 
                data governance, and test-and-learn culture. Includes 5 capstone learning objectives 
                aligned with DHS government applications.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label="Analytics Maturity" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
                <Chip label="Data Governance" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
                <Chip label="Leadership & Change" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
                <Chip label="Innovation" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
                <Chip label="Strategic Resilience" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: '#fff',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/admin/case-studies/maf');
                }}
              >
                View Case Study
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="capstone project tabs">
          <Tab label={`Active (${activeProjects.length})`} />
          <Tab label={`Completed (${completedProjects.length})`} />
          <Tab label={`Drafts (${draftProjects.length})`} />
          <Tab label="All Projects" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {activeProjects.length === 0 ? (
          <Alert severity="info">
            No active projects. Click "New Capstone Project" to get started!
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {activeProjects.map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.projectId}>
                <ProjectCard project={project} onView={handleViewProject} getStatusColor={getStatusColor} calculateProgress={calculateOverallProgress} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {completedProjects.length === 0 ? (
          <Alert severity="info">
            No completed projects yet. Keep working on your active projects!
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {completedProjects.map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.projectId}>
                <ProjectCard project={project} onView={handleViewProject} getStatusColor={getStatusColor} calculateProgress={calculateOverallProgress} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {draftProjects.length === 0 ? (
          <Alert severity="info">
            No draft projects.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {draftProjects.map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.projectId}>
                <ProjectCard project={project} onView={handleViewProject} getStatusColor={getStatusColor} calculateProgress={calculateOverallProgress} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {projects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SchoolIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
              Welcome to Capstone Projects!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start your data strategy learning journey by creating your first capstone project.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleCreateProject}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              Create Your First Project
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.projectId}>
                <ProjectCard project={project} onView={handleViewProject} getStatusColor={getStatusColor} calculateProgress={calculateOverallProgress} />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>
    </Container>
  );
};

// Project Card Component
interface ProjectCardProps {
  project: CapstoneProject;
  onView: (projectId: string) => void;
  getStatusColor: (status: CapstoneProjectStatus) => string;
  calculateProgress: (project: CapstoneProject) => number;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onView, getStatusColor, calculateProgress }) => {
  const progress = calculateProgress(project);
  
  console.log('ProjectCard - Rendering project:', {
    projectId: project.projectId,
    projectName: project.projectName,
    projectIdType: typeof project.projectId
  });
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {project.projectName}
          </Typography>
          <Chip
            label={project.status}
            size="small"
            sx={{
              backgroundColor: getStatusColor(project.status),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {project.uscisUseCase}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              {progress}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
        
        <Typography variant="caption" color="text.secondary">
          Started: {new Date(project.timeline.startDate).toLocaleDateString()}
        </Typography>
      </CardContent>
      
      <CardActions sx={{ pt: 0 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            console.log('View Project clicked! projectId:', project.projectId);
            onView(project.projectId);
          }}
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          View Project
        </Button>
      </CardActions>
    </Card>
  );
};

export default CapstoneProjects;
